/**
 * PurchaseInvoiceAgstPBScreen — Purchase Invoice Against Party Bill (4.21.2)
 * Field structure aligned to legacy sp_PurchaseInvoice_InsertIntoTablesWithXML profiler trace
 * (MasterRow: PIDate/InvNo=PBNo/Ven_CashID/ACId/PITotal... TransDet: PrevItemCode/itemCode/
 *  Qty/Rate/goodsrec/TransType='PI'/Line_Tax_Amt/CGSTPer/SGSTPer/IGSTPer...)
 * Selecting a Party Bill (via ERPLookupModal) hydrates the header + Item Info grid from
 * sp_PartyBill_LoadChildDetails equivalent data — items arrive read-only, only the
 * "Qty Received" column is editable to support partial receipt against the PB.
 */
import React, { useState, useMemo } from 'react';
import { Save, Grid3X3, XCircle, Search } from 'lucide-react';
import ERPAdvancedGrid from '../../../components/erp/ERPAdvancedGrid';
import ERPEditableGrid from '../../../components/erp/ERPEditableGrid';
import ERPLookupModal from '../../../components/erp/ERPLookupModal';
import DynamicFieldRenderer from '../../../components/erp/DynamicFieldRenderer';
import { erpToast } from '../../../components/erp/ERPToast';
import { FormHelp } from '../../../components/help-engine/FormHelp';
import { HelpInfo } from '../../../components/help-engine/HelpInfo';
import '../../../styles/erp-theme.css';
import { tkn, fmtN, autoSelect, today, PARTY_BILL_COLUMNS, fetchPartyBills, updateSessionPartyBill } from './usePartyBillMasters';

const computeRowValue = (row) => {
  const qty = parseFloat(row.qtyReceived) || 0, rate = parseFloat(row.rate) || 0;
  const taxable = qty * rate;
  const gstAmt = taxable * (parseFloat(row.gstPer) || 0) / 100;
  return { ...row, value: (taxable + gstAmt).toFixed(2) };
};

const itemColumns = () => [
  { field: 'itemCode', header: 'Item Code', width: 90, readOnly: true },
  { field: 'itemName', header: 'Item Name', width: 150, readOnly: true },
  { field: 'hsnCode', header: 'HSN Code', width: 80, readOnly: true },
  { field: 'qtyOrdered', header: 'Qty (PB)', width: 75, readOnly: true },
  { field: 'qtyReceived', header: 'Qty Received', width: 90, type: 'number' },
  { field: 'rate', header: 'Rate', width: 80, readOnly: true },
  { field: 'mrp', header: 'MRP', width: 80, readOnly: true },
  { field: 'gstPer', header: 'GST %', width: 60, readOnly: true },
  { field: 'value', header: 'Value', width: 90, readOnly: true },
];

// ═══════════════════════════════════════════════════════
// LIST VIEW — session demo grid
// ═══════════════════════════════════════════════════════
const PIAgstPBGrid = ({ rows, onAddNew, onView }) => {
  const columns = [
    { field: 'piNo', header: 'PI No' }, { field: 'piDate', header: 'PI Date' }, { field: 'pbNo', header: 'Against PB' },
    { field: 'supplierName', header: 'Supplier' }, { field: 'netAmount', header: 'Net Amt' }, { field: 'status', header: 'Status' },
  ];
  return <ERPAdvancedGrid title="Purchase Invoice Against PB" columns={columns} data={rows} total={rows.length} idField="piNo" nameField="supplierName" page={1} pageSize={20} onPageChange={() => {}} onPageSizeChange={() => {}} loading={false} onAddNew={onAddNew} onEdit={(row) => onView(row)} testId="pi-agst-pb-grid" showFilters={false} cellRenderer={(field, value) => (field === 'netAmount' ? <span style={{ fontFamily: 'var(--erp-font-mono)' }}>{fmtN(value)}</span> : null)} />;
};

// ═══════════════════════════════════════════════════════
// FORM VIEW
// ═══════════════════════════════════════════════════════
const PIAgstPBForm = ({ editData, mode, onGridView, onSaved }) => {
  const isView = mode === 'view';
  const token = tkn();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();

  const [piNo, setPiNo] = useState(editData?.piNo || '');
  const [piDate, setPiDate] = useState(editData?.piDate || today());
  const [refNo, setRefNo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedPB, setSelectedPB] = useState(null);
  const [itemRows, setItemRows] = useState([]);
  const [freight, setFreight] = useState('0');
  const [roundOff, setRoundOff] = useState('0');
  const [dynValues, setDynValues] = useState({});
  const [showPBLookup, setShowPBLookup] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleRowsChange = (newRows) => setItemRows(newRows.map(computeRowValue));

  const handlePBSelect = (pb) => {
    setSelectedPB(pb);
    setRefNo(pb.pbNo);
    setItemRows((pb.items || []).map(it => computeRowValue({ itemCode: it.itemCode, itemName: it.itemName, hsnCode: it.hsnCode, qtyOrdered: it.qty, qtyReceived: it.qty, rate: it.rate, mrp: it.mrp, gstPer: it.gstPer })));
    setShowPBLookup(false);
  };

  const grossAmount = useMemo(() => itemRows.reduce((s, r) => s + (parseFloat(r.qtyReceived) || 0) * (parseFloat(r.rate) || 0), 0), [itemRows]);
  const gstTotal = useMemo(() => itemRows.reduce((s, r) => s + (parseFloat(r.qtyReceived) || 0) * (parseFloat(r.rate) || 0) * (parseFloat(r.gstPer) || 0) / 100, 0), [itemRows]);
  const netAmount = grossAmount + gstTotal + (parseFloat(freight) || 0) + (parseFloat(roundOff) || 0);

  const handleClear = () => { setRefNo(''); setDueDate(''); setRemarks(''); setSelectedPB(null); setItemRows([]); setFreight('0'); setRoundOff('0'); setError(''); };

  const handleSave = () => {
    if (!selectedPB) { setError('Select a Party Bill to invoice against'); erpToast.warning('Party Bill reference is required'); return; }
    if (itemRows.length === 0) { setError('No items to invoice'); return; }
    setSaving(true); setError('');
    const newPiNo = piNo || `IAPB${Date.now().toString().slice(-8)}`;
    setPiNo(newPiNo);
    updateSessionPartyBill(selectedPB.pbNo, { status: 'Invoiced' });
    onSaved({ piNo: newPiNo, piDate, pbNo: selectedPB.pbNo, supplierName: selectedPB.supplierName, netAmount: netAmount.toFixed(2), status: 'Posted', items: itemRows });
    erpToast.success(`Purchase Invoice ${newPiNo} saved against ${selectedPB.pbNo} (backend persistence pending next phase)`);
    setSaving(false);
  };

  return (
    <FormHelp formName="PurchaseInvoiceAgstPBScreen" userRole={userRole}>
    <div className="erp-module" data-testid="pi-agst-pb-form" data-dev-component="PurchaseInvoiceAgstPBScreen" data-dev-file="screens/procurement/party-bill/PurchaseInvoiceAgstPBScreen.js">
      <div className="erp-screen-header">
        {onBack ? (<button onClick={onBack} data-testid="screen-back-btn" style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:"2px 0"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 5-7 7 7 7"/></svg><span className="erp-screen-title" style={{color:"var(--erp-accent,#1856a7)"}}>Purchase Invoice Against PB</span></button>) : (<span className="erp-screen-title">Purchase Invoice Against PB</span>)}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color: 'var(--erp-primary)', background: 'var(--erp-hover)', padding: '2px 12px', borderRadius: 4 }} data-testid="pi-no-display">{piNo || 'Auto-Generated'}</div>
          <input type="date" value={piDate} onChange={e => setPiDate(e.target.value)} disabled={isView} className="erp-input" style={{ width: 140, height: 26 }} data-testid="pi-date-header" />
        </div>
        <div className="erp-screen-actions">
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving} data-testid="pi-save-btn"><Save size={13} /> {saving ? 'Saving...' : 'Save'}</button>}
          <button className="erp-btn erp-btn-ghost" onClick={onGridView} data-testid="pi-gridview-btn"><Grid3X3 size={13} /> Gridview</button>
          {!isView && <button className="erp-btn erp-btn-ghost" onClick={handleClear} data-testid="pi-clear-btn"><XCircle size={13} /> Clear</button>}
        </div>
      </div>

      {error && <div className="erp-alert-error" data-testid="pi-error">{error}</div>}

      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }}>
        <div className="erp-form-card" style={{ marginBottom: 8 }}>
          <div className="erp-form-section">
            <div className="erp-form-3col">
              <div>
                <div className="erp-form-row"><label className="erp-form-label required">Party Bill No<HelpInfo controlId="pbNo" controlName="Party Bill No" /></label><div className="erp-form-control" style={{ gap: 4 }}><input value={selectedPB?.pbNo || ''} readOnly placeholder="Search Party Bill..." className="erp-input" style={{ flex: 1 }} onClick={() => !isView && setShowPBLookup(true)} data-testid="pi-pbno-input" /><button className="erp-btn-icon" onClick={() => !isView && setShowPBLookup(true)} data-testid="pi-pbno-search-btn"><Search size={13} /></button></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Ref No<HelpInfo controlId="refNo" controlName="Ref No" /></label><div className="erp-form-control"><input value={refNo} onChange={e => setRefNo(e.target.value)} disabled={isView} className="erp-input" data-testid="pi-refno-input" /></div></div>
                <div className="erp-form-row"><label className="erp-form-label">Due Date<HelpInfo controlId="dueDate" controlName="Due Date" /></label><div className="erp-form-control"><input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={isView} className="erp-input" data-testid="pi-duedate-input" /></div></div>
              </div>
              <div>
                <div className="erp-form-row"><label className="erp-form-label">Remarks<HelpInfo controlId="remarks" controlName="Remarks" /></label><div className="erp-form-control"><input value={remarks} onChange={e => setRemarks(e.target.value)} disabled={isView} className="erp-input" data-testid="pi-remarks-input" /></div></div>
              </div>
              <div>
                <div style={{ border: '1px solid var(--erp-border)', borderRadius: 'var(--erp-radius)', padding: 'var(--erp-sp-2)', fontSize: 11 }} data-testid="pi-pb-info-panel">
                  <div style={{ fontWeight: 600, marginBottom: 4, textDecoration: 'underline' }}>Party Bill Info</div>
                  <div>Supplier: <strong>{selectedPB?.supplierName || '—'}</strong></div>
                  <div>PB Date: <strong>{selectedPB?.pbDate || '—'}</strong></div>
                  <div>PB Total: <strong>{selectedPB?.netAmount ? fmtN(selectedPB.netAmount) : '—'}</strong></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DynamicFieldRenderer screenName="PurchaseInvoiceAgstPBScreen" recordId={piNo} token={token} values={dynValues} onChange={(k, v) => setDynValues(p => ({ ...p, [k]: v }))} readOnly={isView} userRole={userRole} />

        <div className="erp-form-card" style={{ marginBottom: 8, marginTop: 8 }}>
          <div style={{ padding: 'var(--erp-sp-2)', fontWeight: 600, fontSize: 12, color: 'var(--erp-primary)' }}>Item Info {!selectedPB && <span style={{ fontWeight: 400, color: 'var(--erp-text-muted)' }}>— select a Party Bill to load items</span>}</div>
          <ERPEditableGrid columns={itemColumns()} rows={itemRows} onChange={handleRowsChange} idField="itemCode" testId="pi-item-grid" showAddRow={false} showDeleteRow={false} footerSummary={[{ field: 'value', type: 'sum', header: 'Total Value' }]} />
        </div>

        <div className="erp-form-card">
          <div className="erp-form-2col" style={{ padding: 'var(--erp-sp-3)' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, textDecoration: 'underline' }}>Bill Details</div>
              <div className="erp-form-row"><label className="erp-form-label">Freight</label><div className="erp-form-control"><input type="number" value={freight} onFocus={autoSelect} onChange={e => setFreight(e.target.value)} disabled={isView} className="erp-input" style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} data-testid="pi-freight-input" /></div></div>
              <div className="erp-form-row"><label className="erp-form-label">Round Off</label><div className="erp-form-control"><input type="number" value={roundOff} onFocus={autoSelect} onChange={e => setRoundOff(e.target.value)} disabled={isView} className="erp-input" style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} data-testid="pi-roundoff-input" /></div></div>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, textDecoration: 'underline' }}>Amount Details</div>
              <div className="erp-form-row"><label className="erp-form-label">Gross Amount</label><div className="erp-form-control"><span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 12 }} data-testid="pi-gross-amt">{fmtN(grossAmount)}</span></div></div>
              <div className="erp-form-row"><label className="erp-form-label">GST Amt</label><div className="erp-form-control"><span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 12 }} data-testid="pi-gst-amt">{fmtN(gstTotal)}</span></div></div>
              <div className="erp-form-row"><label className="erp-form-label" style={{ fontWeight: 700 }}>Net Amount</label><div className="erp-form-control"><span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--erp-primary)' }} data-testid="pi-net-amt">{fmtN(netAmount)}</span></div></div>
            </div>
          </div>
        </div>
      </div>

      {showPBLookup && <ERPLookupModal title="PARTY BILL LOOKUP" columns={PARTY_BILL_COLUMNS} fetchData={fetchPartyBills} onSelect={handlePBSelect} onClose={() => setShowPBLookup(false)} />}
    </div>
    </FormHelp>
  );
};

// ═══════════════════════════════════════════════════════
// SCREEN CONTROLLER
// ═══════════════════════════════════════════════════════
const PurchaseInvoiceAgstPBScreen = ({ theme = 'light', onBack }) => {
  const [view, setView] = useState('grid');
  const [savedRows, setSavedRows] = useState([]);
  const [editData, setEditData] = useState(null);
  const [formMode, setFormMode] = useState('add');

  if (view === 'grid') return <PIAgstPBGrid rows={savedRows} onAddNew={() => { setEditData(null); setFormMode('add'); setView('form'); }} onView={(row) => { setEditData(row); setFormMode('view'); setView('form'); }} />;
  return <PIAgstPBForm editData={editData} mode={formMode} onGridView={() => setView('grid')} onSaved={(row) => { setSavedRows(prev => [row, ...prev.filter(r => r.piNo !== row.piNo)]); setView('grid'); }} />;
};

export default PurchaseInvoiceAgstPBScreen;
